import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva } from "class-variance-authority"
import { Spinner } from "@volca/ui/components/ui/spinner"
import { cn } from "@volca/ui/lib/utils"

const buttonShape = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-danger-line aria-invalid:ring-3 aria-invalid:ring-danger/20 data-[loading=true]:[&>svg]:hidden [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4!",
  {
    variants: {
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-md px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3!",
        sm: "h-7 gap-1 rounded-md px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5!",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-md in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3!",
        "icon-sm":
          "size-7 rounded-md in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

type ButtonColor = "neutral" | "brand" | "danger"

const DANGER_FOCUS =
  "focus-visible:border-danger-line focus-visible:ring-danger/20"

const VARIANTS = {
  default: {
    defaultColor: "brand",
    colors: {
      brand:
        "bg-brand-bold text-on-brand hover:bg-brand-bold-hover active:bg-brand-bold-pressed",
      neutral:
        "bg-inverse text-on-inverse hover:bg-inverse/90 active:bg-inverse/80",
      danger: cn(
        "bg-danger-bold text-on-danger hover:bg-danger-bold-hover active:bg-danger-bold-pressed",
        DANGER_FOCUS
      ),
    },
  },
  secondary: {
    defaultColor: "neutral",
    colors: {
      neutral:
        "bg-neutral text-ink hover:bg-neutral-hovered active:bg-neutral-pressed aria-expanded:bg-neutral-pressed",
      brand:
        "bg-brand-subtle text-brand hover:bg-brand-subtle-hovered active:bg-brand-subtle-pressed aria-expanded:bg-brand-subtle-pressed",
      danger: cn(
        "bg-danger-subtle text-danger hover:bg-danger-hovered active:bg-danger-pressed aria-expanded:bg-danger-pressed",
        DANGER_FOCUS
      ),
    },
  },
  outline: {
    defaultColor: "neutral",
    colors: {
      neutral:
        "border-line bg-surface-raised text-ink hover:bg-neutral-hovered active:bg-neutral-pressed aria-expanded:bg-neutral-pressed",
      brand:
        "border-brand-line bg-surface-raised text-brand hover:bg-brand-subtle-hovered active:bg-brand-subtle-pressed aria-expanded:bg-brand-subtle-pressed",
      danger: cn(
        "border-danger-line bg-surface-raised text-danger hover:bg-danger-hovered active:bg-danger-pressed aria-expanded:bg-danger-pressed",
        DANGER_FOCUS
      ),
    },
  },
  ghost: {
    defaultColor: "neutral",
    colors: {
      neutral:
        "text-ink hover:bg-neutral-subtle-hovered active:bg-neutral-subtle-pressed aria-expanded:bg-neutral-subtle-pressed",
      brand:
        "text-brand hover:bg-brand-subtle-hovered active:bg-brand-subtle-pressed aria-expanded:bg-brand-subtle-pressed",
      danger: cn(
        "text-danger hover:bg-danger-hovered active:bg-danger-pressed aria-expanded:bg-danger-pressed",
        DANGER_FOCUS
      ),
    },
  },
  link: {
    defaultColor: "brand",
    colors: {
      brand: "text-brand underline-offset-4 hover:underline",
      neutral: "text-ink underline-offset-4 hover:underline",
      danger: "text-danger underline-offset-4 hover:underline",
    },
  },
} as const satisfies Record<
  string,
  { defaultColor: ButtonColor; colors: Record<ButtonColor, string> }
>

type ButtonVariant = keyof typeof VARIANTS
type ButtonSize = NonNullable<Parameters<typeof buttonShape>[0]>["size"]

interface ButtonVariantProps {
  variant?: ButtonVariant | null
  color?: ButtonColor | null
  size?: ButtonSize | null
  className?: string
}

const buttonVariants = ({
  variant,
  color,
  size,
  className,
}: ButtonVariantProps = {}) => {
  const recipe = VARIANTS[variant ?? "default"]
  return cn(
    buttonShape({ size }),
    recipe.colors[color ?? recipe.defaultColor],
    className
  )
}

type ButtonProps = Omit<ButtonPrimitive.Props, "color"> &
  Omit<ButtonVariantProps, "className"> & {
    className?: string
    loading?: boolean
    loadingLabel?: string
  }

function Button({
  className,
  variant = "default",
  color,
  size = "default",
  loading = false,
  disabled,
  children,
  "aria-busy": ariaBusy,
  "aria-label": ariaLabel,
  loadingLabel,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-loading={loading || undefined}
      className={buttonVariants({ variant, color, size, className })}
      disabled={disabled || loading}
      aria-busy={ariaBusy ?? (loading || undefined)}
      aria-label={loading && loadingLabel ? loadingLabel : ariaLabel}
      {...props}
    >
      {loading && <Spinner aria-hidden data-icon="inline-start" />}
      {children}
    </ButtonPrimitive>
  )
}

export {
  Button,
  buttonVariants,
  type ButtonColor,
  type ButtonProps,
  type ButtonSize,
  type ButtonVariant,
}
